import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { PRODUCT_BRAND, ProductMark } from "../../brand/productBrand";
import { StatusBadge } from "../../design-system/components/StatusBadge";
import {
  apiClient,
  clearCachedSession,
  SESSION_CHANGED_EVENT,
} from "../pages/backend_url";
import Iconify from "../pages/login/Iconify";
import { shareablePageUrl } from "../navigation/urlPrivacy";
import { resolveWorkspaceDisplayName } from "./shellPolicy";
import { translate, useLanguage } from "../../localization/LanguageContext";
import LanguageSelector from "../../localization/LanguageSelector";
import "./Navbar.css";

const PROFILE_PANEL_WIDTH = 304;
const PROFILE_PANEL_PREFERRED_HEIGHT = 336;
const COMMAND_PANEL_WIDTH = 356;
const COMMAND_PANEL_PREFERRED_HEIGHT = 430;
const PRIMARY_NAVIGATION_ID = "crm-primary-navigation";
const PRIMARY_NAVIGATION_TOGGLE_ID = "crm-primary-navigation-toggle";
const FOCUSABLE_ELEMENTS = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const readLoggedUser = () => {
  try {
    const raw = sessionStorage.getItem("user_token");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const titleCase = (value) => {
  const cleaned = String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  if (!cleaned) return "Dashboard";

  return cleaned
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getInitials = (name, email) => {
  const value = String(name || email || "User").trim();

  if (!value) return "U";
  if (value.includes("@")) return value.slice(0, 2).toUpperCase();

  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const getFloatingPosition = (
  anchor,
  width,
  preferredHeight = PROFILE_PANEL_PREFERRED_HEIGHT
) => {
  if (!anchor || typeof window === "undefined") {
    return { top: 72, bottom: null, right: 16, width, maxHeight: 520 };
  }

  const rect = anchor.getBoundingClientRect();
  const safePadding = 12;
  const gap = 8;
  const viewportWidth = Math.max(1, Number(window.innerWidth) || 1024);
  const viewportHeight = Math.max(1, Number(window.innerHeight) || 768);
  const availableWidth = Math.max(1, viewportWidth - safePadding * 2);
  const panelWidth = Math.min(
    Math.max(width, rect.width),
    availableWidth
  );
  const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - safePadding);
  const spaceAbove = Math.max(0, rect.top - gap - safePadding);
  const openAbove =
    spaceBelow < preferredHeight && spaceAbove > spaceBelow;
  const availableHeight = openAbove ? spaceAbove : spaceBelow;

  return {
    top: openAbove ? null : Math.max(safePadding, rect.bottom + gap),
    bottom: openAbove
      ? Math.max(safePadding, viewportHeight - rect.top + gap)
      : null,
    right: Math.max(safePadding, viewportWidth - rect.right),
    width: panelWidth,
    maxHeight: Math.max(1, availableHeight),
  };
};

const copyText = async (value) => {
  const text = String(value || "").trim();
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Continue to the legacy browser fallback.
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

function FloatingPortal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

export default function Navbar({
  theme,
  onToggleTheme,
  navigationOpen = false,
  navigationModalOpen = false,
  onToggleNavigation,
  homePath = "/Dashboard",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, moduleLabel } = useLanguage();
  const profileRef = useRef(null);
  const profilePanelRef = useRef(null);
  const commandRef = useRef(null);
  const commandPanelRef = useRef(null);
  const commandSearchRef = useRef(null);
  const activeTriggerRef = useRef(null);

  const [openProfile, setOpenProfile] = useState(false);
  const [openCommand, setOpenCommand] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [profilePosition, setProfilePosition] = useState(() =>
    getFloatingPosition(null, PROFILE_PANEL_WIDTH)
  );
  const [commandPosition, setCommandPosition] = useState(() =>
    getFloatingPosition(null, COMMAND_PANEL_WIDTH, COMMAND_PANEL_PREFERRED_HEIGHT)
  );
  const [toast, setToast] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [loggedUser, setLoggedUser] = useState(readLoggedUser);
  const userName =
    loggedUser?.userName ||
    loggedUser?.name ||
    loggedUser?.fullName ||
    loggedUser?.firstName ||
    "User";
  const userEmail = loggedUser?.userEmail || loggedUser?.email || loggedUser?.mail || "";
  const userRole =
    loggedUser?.role ||
    loggedUser?.userRole ||
    loggedUser?.accessRole ||
    loggedUser?.accountType ||
    "User";
  const workspaceLabel = resolveWorkspaceDisplayName(loggedUser);
  const effectiveTheme = theme === "dark" ? "dark" : "light";
  const isDark = effectiveTheme === "dark";
  const initials = getInitials(userName, userEmail);
  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const normalizedHome = homePath === "/AccountSetup" ? "/AccountSetup" : "/Dashboard";
    const homeLabel = moduleLabel(
      normalizedHome === "/AccountSetup" ? "Account Setup" : "Dashboard"
    );
    if (!segments.length || location.pathname.toLowerCase() === normalizedHome.toLowerCase()) {
      return [{ label: homeLabel, path: normalizedHome, active: true }];
    }

    return [
      { label: homeLabel, path: normalizedHome, active: false },
      ...segments.map((segment, index) => ({
        label: moduleLabel(titleCase(segment)),
        path: `/${segments.slice(0, index + 1).join("/")}`,
        active: index === segments.length - 1,
      })),
    ];
  }, [homePath, location.pathname, moduleLabel]);

  const workspaceShortcuts = useMemo(
    () => [
      {
        path: "/Dashboard",
        label: moduleLabel("Dashboard"),
        description: t("nav.commandDashboardDescription", {}, "Live service overview"),
        icon: "mdi:view-dashboard-outline",
      },
      {
        path: "/EmailTickets",
        label: moduleLabel("Email Tickets"),
        description: t("nav.commandEmailTicketsDescription", {}, "Review and work customer conversations"),
        icon: "mdi:email-multiple-outline",
      },
      {
        path: "/Reports",
        label: moduleLabel("Reports"),
        description: t("nav.commandReportsDescription", {}, "Analyze governed operational evidence"),
        icon: "mdi:chart-box-outline",
      },
      {
        path: "/QualityAssurance",
        label: moduleLabel("Quality Assurance"),
        description: t("nav.commandQualityDescription", {}, "Review quality and coaching work"),
        icon: "mdi:clipboard-check-outline",
      },
      {
        path: "/OperationalPlaybooks",
        label: moduleLabel("Operational Playbooks"),
        description: t("nav.commandPlaybooksDescription", {}, "Use reviewed guides for high-stakes service work"),
        icon: "mdi:book-check-outline",
      },
      {
        path: "/ActionCenter",
        label: moduleLabel("Action Center"),
        description: t("nav.commandActionCenterDescription", {}, "Review prioritized workspace signals"),
        icon: "mdi:bell-badge-outline",
      },
      {
        path: "/AutomationStudio",
        label: moduleLabel("Automation Studio"),
        description: t("nav.commandAutomationDescription", {}, "Design and safely simulate governed service rules"),
        icon: "mdi:robot-outline",
      },
    ],
    [moduleLabel, t]
  );
  const visibleWorkspaceShortcuts = useMemo(() => {
    const query = commandQuery.trim().toLocaleLowerCase();
    if (!query) return workspaceShortcuts;

    return workspaceShortcuts.filter((shortcut) => (
      `${shortcut.label} ${shortcut.description}`.toLocaleLowerCase().includes(query)
    ));
  }, [commandQuery, workspaceShortcuts]);

  const showToast = useCallback((message, type = "info") => {
    setToast({ message, type });
  }, []);

  const closeFloatingPanels = useCallback((restoreFocus = false) => {
    const trigger = activeTriggerRef.current;
    setOpenProfile(false);
    setOpenCommand(false);
    setCommandQuery("");
    activeTriggerRef.current = null;

    if (restoreFocus && trigger) {
      window.requestAnimationFrame(() => trigger.focus());
    }
  }, []);

  useEffect(() => {
    if (navigationModalOpen) closeFloatingPanels(false);
  }, [closeFloatingPanels, navigationModalOpen]);

  const blockGuardedTopbarInteraction = useCallback(
    (event) => {
      if (!navigationModalOpen) return;
      event.preventDefault();
      event.stopPropagation();
    },
    [navigationModalOpen]
  );

  const guardedTopbarProps = navigationModalOpen
    ? {
        inert: "",
        "aria-hidden": "true",
        "data-navigation-inert": "true",
        onClickCapture: blockGuardedTopbarInteraction,
        onPointerDownCapture: blockGuardedTopbarInteraction,
      }
    : {};

  useEffect(() => {
    const refreshSessionUser = () => {
      // A workspace/session refresh can replace the active account without a
      // full page reload. Dismiss transient panels that may describe the old
      // context, but leave the primary navigation state to the shell-owned
      // preference so the rail never unexpectedly collapses.
      closeFloatingPanels(false);
      setLoggedUser(readLoggedUser());
    };
    window.addEventListener(SESSION_CHANGED_EVENT, refreshSessionUser);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, refreshSessionUser);
  }, [closeFloatingPanels]);

  useEffect(() => {
    if (!toast) return undefined;
    if (toast.type === "error") return undefined;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const refreshFloatingPositions = useCallback(() => {
    if (openProfile) {
      setProfilePosition(getFloatingPosition(profileRef.current, PROFILE_PANEL_WIDTH));
    }
    if (openCommand) {
      setCommandPosition(
        getFloatingPosition(
          commandRef.current,
          COMMAND_PANEL_WIDTH,
          COMMAND_PANEL_PREFERRED_HEIGHT
        )
      );
    }
  }, [openCommand, openProfile]);

  useEffect(() => {
    if (!openProfile && !openCommand) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (
        profileRef.current?.contains(target) ||
        profilePanelRef.current?.contains(target) ||
        commandRef.current?.contains(target) ||
        commandPanelRef.current?.contains(target)
      ) {
        return;
      }
      closeFloatingPanels(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeFloatingPanels(true);
        return;
      }

    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", refreshFloatingPositions);
    window.addEventListener("scroll", refreshFloatingPositions, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", refreshFloatingPositions);
      window.removeEventListener("scroll", refreshFloatingPositions, true);
    };
  }, [closeFloatingPanels, openCommand, openProfile, refreshFloatingPositions]);

  useEffect(() => {
    const activePanel = openCommand ? commandPanelRef.current : profilePanelRef.current;
    if (!activePanel) return undefined;

    const frame = window.requestAnimationFrame(() => {
      if (openCommand && commandSearchRef.current) {
        commandSearchRef.current.focus();
        return;
      }
      activePanel.querySelector(FOCUSABLE_ELEMENTS)?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openCommand, openProfile]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleProfilePanel = () => {
    if (openProfile) {
      closeFloatingPanels(false);
      return;
    }

    activeTriggerRef.current = profileRef.current;
    setProfilePosition(getFloatingPosition(profileRef.current, PROFILE_PANEL_WIDTH));
    setOpenCommand(false);
    setOpenProfile(true);
  };

  const toggleCommandPanel = useCallback(() => {
    if (openCommand) {
      closeFloatingPanels(false);
      return;
    }

    activeTriggerRef.current = commandRef.current;
    setCommandQuery("");
    setCommandPosition(
      getFloatingPosition(
        commandRef.current,
        COMMAND_PANEL_WIDTH,
        COMMAND_PANEL_PREFERRED_HEIGHT
      )
    );
    setOpenProfile(false);
    setOpenCommand(true);
  }, [closeFloatingPanels, openCommand]);

  useEffect(() => {
    const onWorkspaceShortcut = (event) => {
      // The mobile rail is a true modal navigation surface. Its focus trap
      // and inert work canvas must remain authoritative; a document-level
      // keyboard shortcut must never open a second dialog behind it.
      if (navigationModalOpen) {
        return;
      }

      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== "k") {
        return;
      }

      const target = event.target;
      const editing = target instanceof HTMLElement && (
        target.matches("input, textarea, select, [contenteditable='true']") ||
        target.closest("[contenteditable='true']")
      );
      if (editing) return;

      event.preventDefault();
      toggleCommandPanel();
    };

    document.addEventListener("keydown", onWorkspaceShortcut);
    return () => document.removeEventListener("keydown", onWorkspaceShortcut);
  }, [navigationModalOpen, toggleCommandPanel]);

  const logout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);

    let serverLogoutConfirmed = false;

    try {
      await apiClient.post("/auth/logout");
      serverLogoutConfirmed = true;
    } catch (error) {
      if (error?.response?.status === 401) {
        // The server already considers this browser session signed out.
        serverLogoutConfirmed = true;
      } else {
        console.warn("Server logout could not be confirmed; retaining the session.", error);
        showToast(
          "Sign out could not be confirmed. Check your connection and try again.",
          "error"
        );
      }
    } finally {
      setLoggingOut(false);
    }

    if (!serverLogoutConfirmed) return;

    clearCachedSession();
    closeFloatingPanels(false);
    navigate("/", { replace: true });
  }, [closeFloatingPanels, loggingOut, navigate, showToast]);

  const handleBrandNavigation = () => {
    closeFloatingPanels(false);
    navigate(homePath);
  };

  const handleWorkspaceShortcut = (path) => {
    closeFloatingPanels(false);
    navigate(path);
  };

  const handleToggleTheme = () => {
    const nextTheme = isDark ? "light" : "dark";
    if (typeof onToggleTheme === "function") onToggleTheme(nextTheme);
    showToast(
      nextTheme === "dark" ? t("nav.darkModeEnabled") : t("nav.lightModeEnabled"),
      "success"
    );
  };

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      showToast(t("nav.fullscreenUnavailable"), "warning");
    }
  };

  const handleCopyEmail = async () => {
    const copied = await copyText(userEmail || userName);
    showToast(
      copied ? t("nav.userDetailsCopied") : t("nav.userDetailsNotCopied"),
      copied ? "success" : "error"
    );
  };

  const handleCopyCurrentPath = async () => {
    const fullPath = shareablePageUrl(window.location.origin, location.pathname);
    const copied = await copyText(fullPath);
    showToast(
      copied ? t("nav.currentPageLinkCopied") : t("nav.currentPageLinkNotCopied"),
      copied ? "success" : "error"
    );
  };

  return (
    <>
      <header
        className="crm-shell-topbar"
        data-theme={effectiveTheme}
        data-navigation-open={navigationOpen ? "true" : "false"}
        data-variant="command-deck"
      >
        <div className="crm-shell-topbar__primary" aria-label="Product navigation">
          {typeof onToggleNavigation === "function" && (
            <button
              id={PRIMARY_NAVIGATION_TOGGLE_ID}
              type="button"
              className="crm-shell-icon-button crm-shell-navigation-toggle"
              onClick={onToggleNavigation}
              aria-label={navigationOpen ? t("nav.closeNavigation") : t("nav.openNavigation")}
              aria-expanded={Boolean(navigationOpen)}
              aria-controls={PRIMARY_NAVIGATION_ID}
              data-open={navigationOpen ? "true" : "false"}
            >
              <Iconify icon={navigationOpen ? "mdi:close" : "mdi:menu"} width={21} />
            </button>
          )}

          <button
            type="button"
            className="crm-shell-brand"
            {...guardedTopbarProps}
            onClick={handleBrandNavigation}
            aria-label={`${PRODUCT_BRAND.fullName} ${homePath === "/AccountSetup" ? "account setup" : "dashboard"}`}
          >
            <span className="crm-shell-brand__mark" aria-hidden="true">
              <ProductMark size={20} />
            </span>
            <span className="crm-shell-brand__copy">
              <span className="crm-shell-brand__name">{PRODUCT_BRAND.name}</span>
              <span className="crm-shell-brand__product">{PRODUCT_BRAND.product}</span>
            </span>
          </button>

          <span className="crm-shell-topbar__divider" aria-hidden="true" />

          <nav
            className="crm-shell-breadcrumbs"
            aria-label="Breadcrumb"
            {...guardedTopbarProps}
          >
            <span className="crm-shell-breadcrumbs__eyebrow" aria-hidden="true">
              {t("nav.nowViewing")}
            </span>
            <ol className="crm-shell-breadcrumbs__list">
              {breadcrumbs.map((item, index) => (
                <li className="crm-shell-breadcrumbs__item" key={item.path || item.label}>
                  {index > 0 && (
                    <Iconify
                      className="crm-shell-breadcrumbs__separator"
                      icon="mdi:chevron-right"
                      width={16}
                      aria-hidden="true"
                    />
                  )}
                  {item.active ? (
                    <span className="crm-shell-breadcrumbs__current" aria-current="page" title={item.label}>
                      {item.label}
                    </span>
                  ) : (
                    <Link className="crm-shell-breadcrumbs__link" to={item.path}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <Link
          to="/AccountSetup"
          className="crm-shell-workspace-context"
          {...guardedTopbarProps}
          aria-label={t("nav.currentCustomerAccount", { workspace: workspaceLabel })}
          aria-current={location.pathname.startsWith("/AccountSetup") ? "page" : undefined}
          title={`${t("nav.currentCustomerAccount", { workspace: workspaceLabel })}. ${t("nav.openAccountSetup")}.`}
        >
          <span className="crm-shell-workspace-context__icon" aria-hidden="true">
            <Iconify icon="mdi:domain" width={17} />
            <span className="crm-shell-workspace-context__presence" />
          </span>
          <span className="crm-shell-workspace-context__copy">
            <span className="crm-shell-workspace-context__label">{t("nav.activeWorkspace")}</span>
            <span className="crm-shell-workspace-context__value" title={workspaceLabel}>
              {workspaceLabel}
            </span>
          </span>
          <Iconify className="crm-shell-workspace-context__arrow" icon="mdi:chevron-right" width={17} aria-hidden="true" />
        </Link>
        <span className="crm-visually-hidden" aria-live="polite" aria-atomic="true">
          {t("nav.activeWorkspace")}: {workspaceLabel}
        </span>

        <div
          className="crm-shell-topbar__actions"
          data-testid="crm-shell-topbar-actions"
          role="group"
          aria-label="Workspace controls"
          {...guardedTopbarProps}
        >
          <LanguageSelector
            onLanguageChanged={(nextLanguage) => showToast(
              translate(nextLanguage.code, "language.changed", { language: nextLanguage.nativeName }),
              "success"
            )}
          />

          <button
            type="button"
            ref={commandRef}
            className="crm-shell-command-trigger"
            data-active={openCommand ? "true" : "false"}
            onClick={toggleCommandPanel}
            aria-haspopup="dialog"
            aria-expanded={openCommand}
            aria-controls="crm-shell-command-panel"
            aria-label={t("nav.openWorkspaceShortcuts", {}, "Open workspace shortcuts")}
            title={t("nav.openWorkspaceShortcuts", {}, "Open workspace shortcuts")}
          >
            <Iconify icon="mdi:apps" width={18} aria-hidden="true" />
            <span className="crm-shell-command-trigger__label">
              {t("nav.shortcuts", {}, "Shortcuts")}
            </span>
            <Iconify
              className="crm-shell-command-trigger__chevron"
              icon="mdi:chevron-down"
              width={16}
              aria-hidden="true"
            />
          </button>

          <button
            type="button"
            className="crm-shell-icon-button"
            onClick={handleToggleTheme}
            title={isDark ? t("nav.switchToLight") : t("nav.switchToDark")}
            aria-label={isDark ? t("nav.switchToLight") : t("nav.switchToDark")}
          >
            <Iconify icon={isDark ? "mdi:white-balance-sunny" : "mdi:weather-night"} width={20} />
          </button>

          <button
            type="button"
            className="crm-shell-icon-button crm-shell-icon-button--fullscreen"
            onClick={handleToggleFullscreen}
            title={isFullscreen ? t("nav.exitFullscreen") : t("nav.enterFullscreen")}
            aria-label={isFullscreen ? t("nav.exitFullscreen") : t("nav.enterFullscreen")}
          >
            <Iconify icon={isFullscreen ? "mdi:arrow-collapse" : "mdi:arrow-expand"} width={20} />
          </button>

          <button
            type="button"
            ref={profileRef}
            className="crm-shell-profile-trigger"
            data-active={openProfile ? "true" : "false"}
            data-state={openProfile ? "expanded" : "collapsed"}
            onClick={toggleProfilePanel}
            aria-haspopup="dialog"
            aria-expanded={openProfile}
            aria-controls="crm-shell-profile-panel"
            title={userEmail || userName}
          >
            <span className="crm-shell-avatar" aria-hidden="true">
              {initials}
            </span>
            <span className="crm-shell-profile-trigger__copy">
              <span className="crm-shell-profile-trigger__name" title={userName}>{userName}</span>
              <span className="crm-shell-profile-trigger__role" title={userRole}>{userRole}</span>
            </span>
            <Iconify
              className="crm-shell-profile-trigger__chevron"
              icon="mdi:chevron-down"
              width={18}
              aria-hidden="true"
            />
          </button>
        </div>
      </header>

      {toast && (
        <div
          className="crm-shell-toast"
          data-tone={toast.type}
          data-theme={effectiveTheme}
          role={toast.type === "error" ? "alert" : "status"}
          aria-live={toast.type === "error" ? "assertive" : "polite"}
        >
          <span className="crm-shell-toast__indicator" aria-hidden="true" />
          <span>{toast.message}</span>
          {toast.type === "error" && (
            <button type="button" onClick={() => setToast(null)} aria-label={t("common.dismiss", {}, "Dismiss message")}>
              <Iconify icon="mdi:close" width={17} />
            </button>
          )}
        </div>
      )}

      {openProfile && (
        <FloatingPortal>
          <div className="crm-shell-popover-backdrop" aria-hidden="true" />
          <section
            ref={profilePanelRef}
            id="crm-shell-profile-panel"
            className="crm-shell-popover crm-shell-profile-panel"
            data-theme={effectiveTheme}
            data-placement={profilePosition.bottom === null ? "below" : "above"}
            style={{
              top: profilePosition.top === null ? "auto" : `${profilePosition.top}px`,
              bottom: profilePosition.bottom === null ? "auto" : `${profilePosition.bottom}px`,
              right: `${profilePosition.right}px`,
              width: `${profilePosition.width}px`,
              maxHeight: `${profilePosition.maxHeight}px`,
            }}
            role="dialog"
            aria-modal="false"
            aria-labelledby="crm-shell-profile-title"
          >
            <div className="crm-shell-profile-panel__identity">
              <span
                className="crm-shell-avatar crm-shell-avatar--large"
                aria-hidden="true"
              >
                {initials}
              </span>
              <div className="crm-shell-profile-panel__identity-copy">
                <h2 id="crm-shell-profile-title" className="crm-shell-profile-panel__name" title={userName}>
                  {userName}
                </h2>
                <p className="crm-shell-profile-panel__email" title={userEmail || t("nav.noEmailAvailable")}>
                  {userEmail || t("nav.noEmailAvailable")}
                </p>
                <StatusBadge tone="info" className="crm-shell-role-badge">
                  {userRole}
                </StatusBadge>
              </div>
              <button
                type="button"
                className="crm-shell-popover__close crm-shell-profile-panel__close"
                onClick={() => closeFloatingPanels(true)}
                aria-label={t("nav.closeProfileMenu")}
              >
                <Iconify icon="mdi:close" width={18} />
              </button>
            </div>

            <div className="crm-shell-popover__divider" />
            <div className="crm-shell-popover__actions">
              <button type="button" className="crm-shell-popover__action" onClick={handleCopyEmail}>
                <Iconify icon="mdi:content-copy" width={18} />
                <span>{t("nav.copyUserDetails")}</span>
              </button>
              <button type="button" className="crm-shell-popover__action" onClick={handleCopyCurrentPath}>
                <Iconify icon="mdi:link-variant" width={18} />
                <span>{t("nav.copyCurrentPageLink")}</span>
              </button>
            </div>

            <div className="crm-shell-popover__actions">
              <button
                type="button"
                className="crm-shell-popover__action crm-shell-popover__action--danger"
                onClick={logout}
                disabled={loggingOut}
              >
                <Iconify icon="mdi:logout" width={18} />
                <span>{loggingOut ? t("nav.signingOut") : t("nav.signOut")}</span>
              </button>
            </div>
          </section>
        </FloatingPortal>
      )}

      {openCommand && (
        <FloatingPortal>
          <div className="crm-shell-popover-backdrop" aria-hidden="true" />
          <section
            ref={commandPanelRef}
            id="crm-shell-command-panel"
            className="crm-shell-popover crm-shell-command-panel"
            data-theme={effectiveTheme}
            data-placement={commandPosition.bottom === null ? "below" : "above"}
            style={{
              top: commandPosition.top === null ? "auto" : `${commandPosition.top}px`,
              bottom: commandPosition.bottom === null ? "auto" : `${commandPosition.bottom}px`,
              right: `${commandPosition.right}px`,
              width: `${commandPosition.width}px`,
              maxHeight: `${commandPosition.maxHeight}px`,
            }}
            role="dialog"
            aria-modal="false"
            aria-labelledby="crm-shell-command-title"
          >
            <div className="crm-shell-command-panel__header">
              <div>
                <p className="crm-shell-command-panel__eyebrow">
                  {t("nav.workspaceCommandDeck", {}, "Workspace command deck")}
                </p>
                <h2 id="crm-shell-command-title" className="crm-shell-command-panel__title">
                  {t("nav.quickActions", {}, "Quick actions")}
                </h2>
                <p className="crm-shell-command-panel__description">
                  {t("nav.quickActionsDescription", {}, "Open a workspace without losing your current account context.")}
                </p>
              </div>
              <button
                type="button"
                className="crm-shell-popover__close crm-shell-command-panel__close"
                onClick={() => closeFloatingPanels(true)}
                aria-label={t("common.dismiss", {}, "Dismiss menu")}
              >
                <Iconify icon="mdi:close" width={18} />
              </button>
            </div>
            <div className="crm-shell-popover__divider" />
            <div className="crm-shell-command-panel__search">
              <Iconify icon="mdi:magnify" width={18} aria-hidden="true" />
              <input
                ref={commandSearchRef}
                type="search"
                value={commandQuery}
                onChange={(event) => setCommandQuery(event.target.value)}
                placeholder={t("nav.searchWorkspaceShortcutsPlaceholder", {}, "Search workspaces")}
                aria-label={t("nav.searchWorkspaceShortcuts", {}, "Search workspace shortcuts")}
                autoComplete="off"
              />
              {commandQuery && (
                <button
                  type="button"
                  className="crm-shell-command-panel__clear-search"
                  onClick={() => setCommandQuery("")}
                  aria-label={t("nav.clearShortcutSearch", {}, "Clear workspace search")}
                >
                  <Iconify icon="mdi:close" width={16} aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="crm-shell-command-panel__shortcuts" aria-label={t("nav.workspaceShortcuts", {}, "Workspace shortcuts")}>
              {visibleWorkspaceShortcuts.map((shortcut) => {
                const current = location.pathname.toLowerCase() === shortcut.path.toLowerCase();
                return (
                  <button
                    key={shortcut.path}
                    type="button"
                    className="crm-shell-command-panel__shortcut"
                    data-current={current ? "true" : "false"}
                    onClick={() => handleWorkspaceShortcut(shortcut.path)}
                    disabled={current}
                  >
                    <span className="crm-shell-command-panel__shortcut-icon" aria-hidden="true">
                      <Iconify icon={shortcut.icon} width={19} />
                    </span>
                    <span className="crm-shell-command-panel__shortcut-copy">
                      <span>{shortcut.label}</span>
                      <small>{shortcut.description}</small>
                    </span>
                    {current ? (
                      <span className="crm-shell-command-panel__current" aria-label={t("nav.currentWorkspace", {}, "Current workspace")}>
                        <Iconify icon="mdi:check" width={16} aria-hidden="true" />
                      </span>
                    ) : (
                      <Iconify className="crm-shell-command-panel__shortcut-arrow" icon="mdi:arrow-top-right" width={17} aria-hidden="true" />
                    )}
                  </button>
                );
              })}
              {visibleWorkspaceShortcuts.length === 0 && (
                <p className="crm-shell-command-panel__empty" role="status">
                  {t("nav.noShortcutResults", {}, "No workspace matches that search.")}
                </p>
              )}
            </div>
          </section>
        </FloatingPortal>
      )}
    </>
  );
}
